import { TrackingSystemParameters } from '@poseidon/tracklib/lib/tracklib.systems';
import Penpal, { ConnectionMethods, IConnectionObject } from 'penpal';
import { Dimensions } from '../../client/interfaces';
import * as Logger from '../../common/tools/logger';
import {
  MonitoringCounterNames,
  MonitoringDurationNames,
  MonitoringEventNames
} from '../../common/tools/monitoring-interfaces';
import { monitoringService } from '../../common/tools/monitoring.service';
import * as tracker from '../../common/tools/tracker';
import { getTimeout } from '../../common/tools/util';
import { CONFIG_CORE_SOFT_NAME } from '../../config';

import {
  _PermissionStatus,
  AditionNextData,
  LayerParameters,
  PermissionData,
  PermissionModel,
} from '../../interfaces';
import { LayerParentApi, PermissionLayer, PermissionLayerOptions, TcData } from '../../layer/interfaces';
import { AditionIframe } from './adition-iframe.interface';
import { buildAditionRequest, getCacheBuster } from './adition.util';
import { CommunicationHandlerService } from './communication-handler.service';

/**
 * Class for all about ADITION
 */
export class AditionService {

  private moduleName = `AditionService`;
  private iframe: HTMLIFrameElement;
  private windowId: string = getCacheBuster(); // Needs to be the same through the lifetime of a PPP instance!
  private communicationHandler: CommunicationHandlerService;
  private childConnection: IConnectionObject<ConnectionMethods>;
  private cycleCount = 1;
  private initTime: number;
  private layerViewTime: number;
  private trackingData: TrackingSystemParameters;
  private layer: LayerParentApi;
  /**
   * true if the publisher page has called layerDisplayed to signal that it has displayed the PPP iframe
   */
  private isPppVisible = false;
  /**
   * true if the currently connected layer's onLayerDisplayed method has already been called
   */
  private layerDisplayedCalled = false;

  /**
   * @private
   * @function childApi
   * @description API exposed to the child (Permission Layer)
   * @return {Partial<PermissionLayer>}
   */
  private childApi: Partial<PermissionLayer> = {
    connect: async (options?: PermissionLayerOptions): Promise<void> => {
      monitoringService.pushCounter({
        name: MonitoringCounterNames.layer,
        event: MonitoringEventNames.connected
      });

      await this.communicationHandler.openPermissionLayer(options);
      this.layerViewTime = Date.now();

      const clientParams = await this.communicationHandler.getLayerParameters();
      this.trackingData = {
        coun_loop: this.cycleCount,
        // tracking data from the client
        ...(clientParams ? clientParams.trackingParameters : {}),
        // tracking data from the layer
        ...(options? options.trackingData : {})
      }

      /* istanbul ignore next if */
      if (options && typeof options.campaignId === 'string' && typeof options.bannerId === 'string') {
        this.trackingData.cont_campaign_banner = `${options.campaignId}_${options.bannerId}`;
      }

      /* istanbul ignore next if */
      if (options && typeof options.layerName === 'string') {
        this.trackingData.cont_layer_name = options.layerName;
      }

      try {
        tracker.track(CONFIG_CORE_SOFT_NAME, {
          ...this.trackingData,
          section: 'permission.layer.ready',
          time_layer_playout: this.layerViewTime - this.initTime,
          sett_sample: 1
        });
      } catch (e) {
        Logger.error(this.moduleName,  'childApi.connect', 'track() call failed', e);
      }

      await this.communicationHandler.openPermissionLayer(options);

      // Publishers will call layerDisplayed only once per PPP lifecycle when the first layer is ready. But since
      // subsequent layers should also receive the onLayerDisplayed signal, we have to trigger it here
      if (this.isPppVisible && !this.layerDisplayedCalled && typeof this.layer.onLayerDisplayed === 'function') {
        this.layer.onLayerDisplayed();
        this.layerDisplayedCalled = true;
      }
    },

    update: async (permissionData: PermissionData | string, status?: _PermissionStatus): Promise<void> => {
      let permStatus: string;
      /* istanbul ignore next */
      if (typeof permissionData === 'string') {
        /* istanbul ignore next */
        Logger.warn(`PermissionLayer.update(): The 'permissionType' and 'status' string parameters are deprecated, please call this method with a PermissionData object as the only argument.`);
        /* istanbul ignore next */
        await this.communicationHandler.updatePermissions([{
          usecase: permissionData,
          status,
        }]);
        /* istanbul ignore next */
        permStatus = status;
      } else {
        await this.communicationHandler.updatePermissions([permissionData]);
        permStatus = permissionData.status;
      }

      const trackParams: TrackingSystemParameters = {
        ...this.trackingData,
        section: permStatus.toUpperCase() === 'VALID' ? 'permission.layer.perm.grant' : 'permission.layer.perm.deny',
        cont_perm_usecase: /* istanbul ignore next if */ typeof permissionData === 'string' ? permissionData : permissionData.usecase,
        time_action: Date.now() - this.layerViewTime,
        sett_sample: 1
      };

      tracker.track(CONFIG_CORE_SOFT_NAME, trackParams);
    },

    setPermission: async (permissionData: PermissionData): Promise<void> => {
      await this.communicationHandler.setPermissions([permissionData]);

      const trackParams: TrackingSystemParameters = {
        ...this.trackingData,
        section: permissionData.status.toUpperCase() === 'VALID' ? 'permission.layer.perm.grant' : 'permission.layer.perm.deny',
        cont_perm_usecase: permissionData.usecase,
        time_action: Date.now() - this.layerViewTime,
        sett_sample: 1
      };

      tracker.track(CONFIG_CORE_SOFT_NAME, trackParams);
    },

    updateTCData: async (tcData: TcData): Promise<void> => {
      await this.communicationHandler.setTCData(tcData);
    },

    next: async (nextData?: AditionNextData): Promise<void> => {
      this.cycleCount = this.cycleCount + 1;
      this.layerDisplayedCalled = false;
      monitoringService.pushCounter({
        name: MonitoringCounterNames.layer,
        event: MonitoringEventNames.next_requested
      });
      monitoringService.flush();

      await this.communicationHandler.updatePermissionLayer(nextData);
    },

    complete: async (): Promise<void> => {
      monitoringService.pushCounter({
        name: MonitoringCounterNames.layer,
        event: MonitoringEventNames.completed
      });

      if (!this.layerViewTime) {
        tracker.track(CONFIG_CORE_SOFT_NAME, {
          ...this.trackingData,
          section: 'permission.layer.nocontent',
          coun_loop: this.cycleCount,
          time_process_complete: Date.now() - this.initTime,
          sett_sample: 100
        });
      }
      this.layerViewTime = null;
      tracker.track(CONFIG_CORE_SOFT_NAME, {
        ...this.trackingData,
        section: 'permission.ation.complete',
        coun_loop: this.cycleCount,
        time_process_complete: Date.now() - this.initTime,
        sett_sample: 100
      });

      await new Promise<void>(((resolve) => {
        setTimeout(() => {
          this.communicationHandler.closePermissionLayer().then(() => {
            this.destroy();
            resolve();
          });
        }, 200);
      }));
    },

    getPermissionModel: (): Promise<PermissionModel> => {
      return new Promise((resolve): void => {
        resolve(this.communicationHandler.permissionModel);
      });
    },

    getParameters: async (): Promise<LayerParameters> => {
      return await this.communicationHandler.getLayerParameters();
    },

    resize: async (dimensions: Dimensions): Promise<void> => {
      await this.communicationHandler.resizePermissionLayer(dimensions);
    },

    trackBrain: async (params: TrackingSystemParameters): Promise<void> => {
      await this.communicationHandler.trackBrain({
        ...this.trackingData,
        ...params
      });
    }
  };

  /**
   * @public
   * @async
   * @function init
   * @description Function to initialize this module
   * @param {PermissionModel} permissionModel The current permission model which will be appended to the ADITION request
   * @param {CommunicationHandlerService} communicationHandler Instance of the `CommunicationHandlerService`
   * @param {AditionNextData} nextData Data to be passed for a single next call
   * @return {Promise<void>}
   */
  public async init(permissionModel: PermissionModel,
                    communicationHandler: CommunicationHandlerService,
                    nextData?: AditionNextData): Promise<void> {
    this.communicationHandler = communicationHandler;

    try {
      /* istanbul ignore next else */
      if (!this.initTime) {
        this.initTime = Date.now();
      }
      await this.loadAditionFrame(permissionModel, nextData);

      Logger.debug(this.moduleName, `init`, `Module initialized.`);
      return;
    } catch (error) {
      await this.communicationHandler.closePermissionLayer(false, error);
    }
  }

  /**
   * @private
   * @async
   * @function loadAditionFrame
   * @description Function to load a adition frame
   * @param {PermissionModel} permissionModel The current permission model
   * @param {AditionNextData} nextData Data to be passed for a single next call
   * @return {Promise<void>}
   */
  private async loadAditionFrame(permissionModel: PermissionModel, nextData?: AditionNextData): Promise<void> {
    let url: string;
    try {
      monitoringService.pushCounter({
        name: MonitoringCounterNames.adition_iframe_load,
        event: MonitoringEventNames.started
      });

      const clientConfig = await this.communicationHandler.getAditionClientConfig();
      const userIdentity = await this.communicationHandler.getIdentity();
      const consentParameters = (await this.communicationHandler.getLayerParameters()).consentParameters;
      url = buildAditionRequest(clientConfig, permissionModel, consentParameters, this.windowId, userIdentity, nextData);
      Logger.debug(this.moduleName, `loadAditionFrame`, `New adition request built: '${url}'.`);

      if (this.iframe) {
        try {
          this.childConnection.destroy();
          // eslint-disable-next-line no-empty
        } catch (ex) {
          // remove the iframe from the DOM if Penpal can't do it
          this.iframe.parentElement.removeChild(this.iframe);
        }
      }

      this.iframe = document.createElement('iframe');
      const iframeAttributes: AditionIframe = {
        border: '0',
        frameborder: '0',
        marginwidth: '0',
        marginheight: '0',
        allowtransparency: 'true',
        background: 'transparent',
        scrolling: 'yes',
        sandbox: 'allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts',
        style: 'display: block; width: 100%; height: 100%; border: 0;'
      };

      Object.keys(iframeAttributes).forEach((attr) => {
        this.iframe.setAttribute(attr, iframeAttributes[attr as keyof AditionIframe]);
      });

      const start = Date.now();
      this.childConnection = Penpal.connectToChild({
        url,
        iframe: this.iframe,
        appendTo: document.body,
        timeout: getTimeout(),
        methods: this.childApi,
      });

      this.layer = await this.childConnection.promise as LayerParentApi;
      monitoringService.pushDuration({
        name: MonitoringDurationNames.adition_iframe_load,
        duration: Date.now() - start
      });

      Logger.debug(this.moduleName, `loadAditionFrame`, `New adition frame created: `, url);
    } catch (error) {
      monitoringService.pushCounter({
        name: MonitoringCounterNames.adition_iframe_load,
        event: MonitoringEventNames.failed
      });
      tracker.track(CONFIG_CORE_SOFT_NAME, {
        ...this.trackingData,
        section: 'permission.layer.ready',
        resu_ation: '44000010',
        sett_sample: 1
      });
      await this.communicationHandler.closePermissionLayer(false, error.toString());

      /* istanbul ignore next else */
      if (error || Object.prototype.hasOwnProperty.call(error, 'message')) {
        const errorMessage: string = (error.message || error).toLowerCase();

        if (errorMessage.indexOf('timed out') > -1 || errorMessage.indexOf('connection destroyed') > -1) {
          Logger.warn(this.moduleName, `loadAditionFrame`, errorMessage);
        } else {
          Logger.error(this.moduleName, `loadAditionFrame`, errorMessage);
        }
      } else {
        Logger.error(this.moduleName, `loadAditionFrame`, error);
      }
    }
  }

  /**
   * Informs the layer and requests a tracking pixel when the layer has been displayed to the user
   * @param trackingData additional tracking data
   */
  public async layerDisplayed(trackingData: { [key: string]: string }): Promise<void> {
    this.isPppVisible = true;
    /* istanbul ignore next else */
    if (this.layer && typeof this.layer.onLayerDisplayed === 'function') {
      this.layerDisplayedCalled = true;
      this.layer.onLayerDisplayed();
    }

    monitoringService.pushCounter({
      name: MonitoringCounterNames.layer,
      event: MonitoringEventNames.displayed
    });

    const clientConfig = await this.communicationHandler.getAditionClientConfig();
    const params: TrackingSystemParameters = {
      section: 'permission.layer.view',
      coun_loop: this.cycleCount,
      time_layer_playout: this.layerViewTime - this.initTime,
      sett_sample: 1,
    }

    /* istanbul ignore next else */
    if (clientConfig.forcePermissionLayer) {
      params['cont_requested_perm'] = clientConfig.forcePermissionLayer;
    } else if (clientConfig.permissionGroup) {
      params['cont_requested_perm'] = clientConfig.permissionGroup;
    }

    /* istanbul ignore next else */
    if (trackingData.touchpointName) {
      params['cont_touchpoint_id'] = trackingData.touchpointName;
    }

    tracker.track(CONFIG_CORE_SOFT_NAME, {
      ...this.trackingData,
      ...params
    });
  }

  /**
   * @public
   * @function destroy
   * @description Function to finish the permission core
   * @return {void}
   */
  public destroy(): void {
    if (this.childConnection) {
      this.childConnection.destroy();
      this.childConnection = null;
    }

    if (this.iframe) {
      this.iframe.src = 'about:blank';
      /* istanbul ignore next else */
      if (this.iframe.parentNode) {
        this.iframe.parentNode.removeChild(this.iframe);
      }
      this.iframe = null;
    }

    this.communicationHandler = null;
  }

}
